/**
 * useSCPermissions — React hook that surfaces the current tenant's Sales
 * Coordinator permission flags to the frontend so Edit / Delete / Reassign
 * / Export / Share buttons can be disabled with a helpful tooltip when the
 * caller is a Sales Coordinator and the action isn't permitted.
 *
 * Backing endpoint: GET /api/sc-permissions
 *
 * The hook caches the first fetch in-module so visiting multiple pages
 * doesn't hit the backend repeatedly. The cache is keyed by tenant and
 * invalidated when the user logs out (via `logout()` on the auth store).
 *
 * Shape returned:
 *   {
 *     isSC: boolean,                    // true iff user.role === 'Sales Coordinator'
 *     flags: { can_export_csv, ... },   // see DEFAULT_SC_PERMISSIONS on backend
 *     loading: boolean,
 *     // Helpers (safe to call for non-SC — they return non-restrictive):
 *     canEditRecord(record),            // owner check for single-record edits
 *     canExport(),
 *     canBulkReassign(),
 *     canBulkDelete(),
 *     canShareLink(),
 *     canViewAnalyticsTab(),
 *     canViewGMNumbers(),
 *     refresh(),                        // force re-fetch (after President flips a flag)
 *   }
 */

import { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import useAuthStore from '../store/authStore';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Defaults mirror backend `DEFAULT_SC_PERMISSIONS` — used before the fetch
// resolves so UI never flashes buttons it will immediately disable.
const DEFAULT_FLAGS = {
  can_export_csv: false,
  can_bulk_reassign: false,
  can_bulk_delete: false,
  can_use_share_links: false,
  can_view_analytics_tab: true,
  can_view_gm_numbers: false,
};

// Module-level cache keyed by tenant_id. Invalidated by `clearSCPermissionsCache()`.
const _cache = { tenantId: null, flags: null, fetchedAt: 0 };

export function clearSCPermissionsCache() {
  _cache.tenantId = null;
  _cache.flags = null;
  _cache.fetchedAt = 0;
}

export default function useSCPermissions() {
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const isSC = user?.role === 'Sales Coordinator';

  const [flags, setFlags] = useState(() =>
    _cache.flags && _cache.tenantId === user?.tenant_id ? _cache.flags : DEFAULT_FLAGS
  );
  const [loading, setLoading] = useState(() => !_cache.flags && !!token);
  const abortRef = useRef(null);

  const fetchFlags = useCallback(async () => {
    if (!token) return;
    try {
      // Abort any in-flight call before issuing a new one (prevents a
      // stale response from overwriting a fresh one after a flag toggle).
      if (abortRef.current) abortRef.current.abort();
      abortRef.current = new AbortController();
      setLoading(true);
      const res = await axios.get(`${API}/sc-permissions`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: abortRef.current.signal,
      });
      const next = { ...DEFAULT_FLAGS, ...(res.data?.flags || {}) };
      _cache.tenantId = res.data?.tenant_id || user?.tenant_id || null;
      _cache.flags = next;
      _cache.fetchedAt = Date.now();
      setFlags(next);
    } catch (e) {
      if (axios.isCancel?.(e) || e.name === 'CanceledError' || e.name === 'AbortError') return;
      // Swallow — leave DEFAULT_FLAGS in place. Backend defaults are safe.
    } finally {
      setLoading(false);
    }
  }, [token, user?.tenant_id]);

  useEffect(() => {
    if (!token) return;
    // Serve from module cache if we've already fetched for this tenant.
    if (_cache.flags && _cache.tenantId === user?.tenant_id) {
      setFlags(_cache.flags);
      setLoading(false);
      return;
    }
    fetchFlags();
  }, [token, user?.tenant_id, fetchFlags]);

  // ---- Helpers (safe for non-SC: always return permissive true) ---------

  const canEditRecord = useCallback(
    (record) => {
      if (!isSC) return true;
      if (!record) return false;
      const uid = user?.id || user?.sub;
      if (!uid) return false;
      return record.created_by === uid || record.assigned_to === uid;
    },
    [isSC, user]
  );

  const canExport = useCallback(() => (!isSC ? true : !!flags.can_export_csv), [isSC, flags]);
  const canBulkReassign = useCallback(() => (!isSC ? true : !!flags.can_bulk_reassign), [isSC, flags]);
  const canBulkDelete = useCallback(() => (!isSC ? true : !!flags.can_bulk_delete), [isSC, flags]);
  const canShareLink = useCallback(() => (!isSC ? true : !!flags.can_use_share_links), [isSC, flags]);
  const canViewAnalyticsTab = useCallback(() => (!isSC ? true : !!flags.can_view_analytics_tab), [isSC, flags]);
  const canViewGMNumbers = useCallback(() => (!isSC ? true : !!flags.can_view_gm_numbers), [isSC, flags]);

  return {
    isSC,
    flags,
    loading,
    canEditRecord,
    canExport,
    canBulkReassign,
    canBulkDelete,
    canShareLink,
    canViewAnalyticsTab,
    canViewGMNumbers,
    refresh: fetchFlags,
  };
}
